/* Who a client or referral is assigned to.
   ========================================

   Extracted because it was just the site of a real bug, per the rule in
   CLAUDE.md: move a function out when you are about to change it, so the
   refactor is paid for by work you wanted anyway. These are pure — no DOM, no
   globals, no Firestore — which is why they can be tested at all, and the bug
   below is precisely the kind a test catches and a browser does not.

   THE BUG. The original read:

     if (Array.isArray(c.assignedUids) && c.assignedUids.length) return c.assignedUids;
     ... else return [c.addedBy];

   so a record explicitly assigned to NOBODY was indistinguishable from one
   that had never been assigned, and both fell back to whoever added it. In the
   UI that played out as:

     remove CHW A  -> assignedUids: []          written to Firestore
     re-render     -> assignedTo() returns [A]  the chip comes back
     add CHW B     -> reads [A], pushes B       writes [A, B]

   A is silently restored and the client never moves off them. It also made the
   profile claim an assignment the database did not have — Firestore rules read
   the real `assignedUids` field, so the record was invisible to A and
   unwritable by them while the screen showed them as assigned.

   THE RULE: an empty array is an answer. Fall back only when the field is
   genuinely absent. */

/* Note the 'null' default rather than '[]': JSON.parse('[]') yields an array,
   which would satisfy the isArray check and swallow the addedBy fallback for
   old records that have neither field. */
function fromLegacyJson(json) {
  try {
    const a = JSON.parse(json || 'null');
    return Array.isArray(a) ? a.slice() : null;
  } catch {
    return null;
  }
}

/* Returns a COPY, always. Callers push onto what they get back
   (clientClaim, clientAddAssignee), and handing out the live cache array let
   them mutate teamClientsCache in place before any write was attempted — so a
   rejected write left the UI showing an assignment that never happened. */
export function clientAssignedTo(c) {
  if (!c) return [];
  if (Array.isArray(c.assignedUids)) return c.assignedUids.slice();
  const legacy = fromLegacyJson(c.assignedJson);
  if (legacy) return legacy;
  return c.addedBy ? [c.addedBy] : [];
}

export function clientIsAssigned(c, uid) {
  return !!uid && clientAssignedTo(c).includes(uid);
}

/* Referrals never had the assignedJson string — they got the array from the
   start — so this is the same rule with one fewer fallback. */
export function referralAssignedTo(r) {
  if (!r) return [];
  if (Array.isArray(r.assignedUids)) return r.assignedUids.slice();
  return r.addedBy ? [r.addedBy] : [];
}

export function referralIsAssigned(r, uid) {
  return !!uid && referralAssignedTo(r).includes(uid);
}
